'use client';

import { useEffect } from 'react';
import { useReportWebVitals } from 'next/web-vitals';

import { debug } from '@/lib/debug';
export default function WebVitals() {
  // Live layout-shift logging — fires as each shift happens, so you don't
  // have to blur the tab to see CLS data. Only in dev.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!('PerformanceObserver' in window)) return;

    let cumulative = 0;
    let sessionValue = 0;
    let sessionEntries: LayoutShift[] = [];

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries() as LayoutShift[]) {
        // Ignore shifts right after user input — they don't count toward CLS
        if (entry.hadRecentInput) continue;

        cumulative += entry.value;

        // Group shifts into sessions (max 5s window, 1s gap) — same rule CLS uses
        const first = sessionEntries[0];
        const last = sessionEntries[sessionEntries.length - 1];
        if (
          sessionEntries.length &&
          last &&
          first &&
          (entry.startTime - last.startTime > 1000 ||
            entry.startTime - first.startTime > 5000)
        ) {
          sessionValue = 0;
          sessionEntries = [];
        }
        sessionValue += entry.value;
        sessionEntries.push(entry);

        const rating =
          cumulative <= 0.1 ? 'good' : cumulative <= 0.25 ? 'needs-improvement' : 'poor';
        const color =
          rating === 'good' ? '#0cce6b' : rating === 'needs-improvement' ? '#ffa400' : '#ff4e42';

        debug.groupCollapsed(
          `%c[web vitals CLS shift] +${entry.value.toFixed(4)}  (total: ${cumulative.toFixed(4)}, ${rating})`,
          `color: ${color}; font-weight: bold;`
        );
        debug.log('startTime:', entry.startTime.toFixed(0), 'ms');
        entry.sources?.forEach((source, i) => {
          debug.log(`source ${i + 1}:`, source.node);
          debug.log('  previousRect:', source.previousRect);
          debug.log('  currentRect: ', source.currentRect);
        });
        debug.groupEnd();
      }
    });

    try {
      observer.observe({ type: 'layout-shift', buffered: true });
    } catch {
      // browser doesn't support layout-shift
    }

    return () => observer.disconnect();
  }, []);

  useReportWebVitals((metric) => {
    // if (process.env.NODE_ENV !== 'development') return;

    const { name, value, rating, entries } = metric;

    if (name === 'CLS') {
      debug.log(
        `%c[Web Vitals] CLS: ${value.toFixed(4)} (${rating})`,
        `color: ${rating === 'good' ? '#0cce6b' : rating === 'needs-improvement' ? '#ffa400' : '#ff4e42'}; font-weight: bold;`,
        { entries }
      );

      // Log each layout shift source so you can see which DOM nodes moved
      (entries as LayoutShift[]).forEach((entry, i) => {
        if (entry.sources?.length) {
          debug.groupCollapsed(`  shift #${i + 1}: value=${entry.value.toFixed(4)}`);
          entry.sources.forEach((source) => {
            debug.log('node:', source.node, 'previousRect:', source.previousRect, 'currentRect:', source.currentRect);
          });
          debug.groupEnd();
        }
      });
    } else {
      debug.log(`[Web Vitals] ${name}: ${typeof value === 'number' ? value.toFixed(2) : value} (${rating})`);
    }
  });

  return null;
}

// Minimal types for layout-shift entries (not in lib.dom yet)
interface LayoutShiftAttribution {
  node?: Node | null;
  previousRect: DOMRectReadOnly;
  currentRect: DOMRectReadOnly;
}

interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  sources: LayoutShiftAttribution[];
}
